// "Przegląd" tab: KPIs, insights, volume and sentiment overview.
import {
  ArrowRight,
  CircleAlert,
  Info,
  Lightbulb,
  ThumbsDown,
  ThumbsUp,
} from "lucide-react";
import ReviewCard from "./ReviewCard";
import { RatingDistributionList, SentimentDonut, VolumeChart } from "./charts";
import { COLORS } from "./constants";
import {
  BarList,
  Card,
  Delta,
  EmptyState,
  KpiCard,
  MiniStat,
  Sparkline,
  Stars,
} from "./ui";
import { formatRange, nf, reviewsWord } from "./utils";

export const InsightList = ({ insights, onDrill }) => {
  if (!insights.length)
    return <EmptyState title="Brak wniosków dla wybranych filtrów" />;
  const icons = {
    positive: ThumbsUp,
    negative: ThumbsDown,
    warning: CircleAlert,
    info: Info,
  };
  return (
    <ul className="gr-insights">
      {insights.map((insight, i) => {
        const Icon = icons[insight.kind] || Lightbulb;
        const hasDrill = Object.keys(insight.filters || {}).length > 0;
        const content = (
          <>
            <span className={`gr-insight-icon gr-insight-${insight.kind}`}>
              <Icon size={16} aria-hidden="true" />
            </span>
            <span className="gr-insight-body">
              <strong>{insight.title}</strong>
              <span>{insight.detail}</span>
            </span>
            {hasDrill && (
              <ArrowRight
                size={16}
                className="gr-insight-arrow"
                aria-hidden="true"
              />
            )}
          </>
        );
        return (
          <li key={i}>
            {hasDrill ? (
              <button
                type="button"
                onClick={() => onDrill(insight.filters)}
                title="Pokaż opinie"
              >
                {content}
              </button>
            ) : (
              <div>{content}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

const OverviewTab = ({
  data,
  attention,
  attentionLoading,
  onDrill,
  onBucket,
  showAttention,
}) => {
  const {
    kpis,
    comparison,
    rating_distribution: ratingDist,
    timeline,
    insights,
    themes,
  } = data;
  const prev = comparison?.kpis;
  const prevTitle = comparison
    ? `Okres porównawczy: ${formatRange(comparison.start_date, comparison.end_date)}`
    : undefined;
  const praise = themes
    .filter((t) => t.praise_mentions > 0 && t.key !== "inne")
    .sort((a, b) => b.praise_mentions - a.praise_mentions)
    .slice(0, 5);
  const complaints = themes
    .filter((t) => t.complaint_mentions > 0)
    .sort((a, b) => b.complaint_mentions - a.complaint_mentions)
    .slice(0, 5);

  return (
    <div className="gr-tab">
      <div className="gr-kpis">
        <KpiCard
          label="Średnia ocena Google"
          value={nf(kpis.average_rating, 2)}
          suffix="/ 5"
          delta={
            prev &&
            prev.total_reviews > 0 && (
              <Delta
                current={kpis.average_rating}
                previous={prev.average_rating}
                digits={2}
                title={prevTitle}
              />
            )
          }
          hint={
            prev && prev.total_reviews > 0
              ? `poprz. ${nf(prev.average_rating, 2)}`
              : `${nf(kpis.five_star_share, 0)}% ocen 5★`
          }
          spark={
            <Sparkline
              data={timeline}
              dataKey="average_rating"
              color={COLORS.accent}
            />
          }
        >
          <Stars value={kpis.average_rating} />
        </KpiCard>
        <KpiCard
          label="Liczba opinii"
          value={nf(kpis.total_reviews)}
          delta={
            prev && (
              <Delta
                current={kpis.total_reviews}
                previous={prev.total_reviews}
                kind="relative"
                title={prevTitle}
              />
            )
          }
          hint={
            prev
              ? `poprz. ${nf(prev.total_reviews)}`
              : `${nf(kpis.reviews_with_text)} z treścią`
          }
          spark={
            <Sparkline data={timeline} dataKey="count" color={COLORS.accent} />
          }
        />
        <KpiCard
          label="Opinie pozytywne"
          value={`${nf(kpis.positive_share, 1)}%`}
          tone="positive"
          delta={
            prev &&
            prev.total_reviews > 0 && (
              <Delta
                current={kpis.positive_share}
                previous={prev.positive_share}
                kind="pp"
                title={prevTitle}
              />
            )
          }
          hint={`${nf(kpis.positive_reviews)} ${reviewsWord(kpis.positive_reviews)}`}
        />
        <KpiCard
          label="Opinie negatywne"
          value={`${nf(kpis.negative_share, 1)}%`}
          tone="negative"
          delta={
            prev &&
            prev.total_reviews > 0 && (
              <Delta
                current={kpis.negative_share}
                previous={prev.negative_share}
                kind="pp"
                goodWhen="down"
                title={prevTitle}
              />
            )
          }
          hint={`${nf(kpis.negative_reviews)} ${reviewsWord(kpis.negative_reviews)}`}
          spark={
            <Sparkline
              data={timeline}
              dataKey="negative_count"
              color={COLORS.negative}
            />
          }
        />
      </div>

      <div className="gr-ministats">
        <MiniStat
          label="Poleciłoby firmę"
          value={`${nf(kpis.recommendation_rate, 1)}%`}
          delta={
            prev &&
            prev.recommendation_known_count > 0 && (
              <Delta
                current={kpis.recommendation_rate}
                previous={prev.recommendation_rate}
                kind="pp"
                title={prevTitle}
              />
            )
          }
          hint={` wg analizy AI, n=${nf(kpis.recommendation_known_count)}`}
        />
        <MiniStat
          label="Problem rozwiązany"
          value={
            kpis.issue_known_count
              ? `${nf(kpis.issue_resolution_rate, 1)}%`
              : "–"
          }
          hint={` z ${nf(kpis.issue_known_count)} opinii z oceną rozwiązania`}
        />
        <MiniStat
          label="Odpowiedzi na negatywne"
          value={
            kpis.negative_reviews ? `${nf(kpis.negative_reply_rate, 0)}%` : "–"
          }
          hint={` ${nf(kpis.negative_with_reply)} z ${nf(kpis.negative_reviews)}`}
        />
        <MiniStat
          label="Wysoka pilność"
          value={nf(kpis.high_urgency_count)}
          hint={` ${reviewsWord(kpis.high_urgency_count)} do pilnej reakcji`}
        />
      </div>

      <div className="gr-grid gr-grid-7-5">
        <Card
          id="gr-insights"
          title="Najważniejsze wnioski"
          subtitle="Wyliczone z danych dla bieżących filtrów – kliknij, aby zobaczyć opinie"
        >
          <InsightList insights={insights} onDrill={onDrill} />
        </Card>
        <Card
          id="gr-rating-dist"
          title="Rozkład ocen"
          subtitle={
            comparison
              ? "Udział ocen vs okres porównawczy"
              : "Liczba i udział opinii według gwiazdek"
          }
          exportName="rozklad-ocen"
        >
          <RatingDistributionList
            distribution={ratingDist}
            previous={comparison?.rating_distribution}
            onSelect={(rating) => onDrill({ rating })}
          />
        </Card>
      </div>

      <div className="gr-grid gr-grid-8-4">
        <Card
          id="gr-volume"
          title="Opinie w czasie"
          subtitle="Liczba opinii według sentymentu – kliknij słupek, aby zobaczyć opinie z okresu"
          exportName="opinie-w-czasie"
        >
          <VolumeChart timeline={timeline} onSelectBucket={onBucket} />
        </Card>
        <Card id="gr-sentiment" title="Sentyment" exportName="sentyment">
          <SentimentDonut
            kpis={kpis}
            onSelect={(sentiment) => onDrill({ sentiment })}
          />
        </Card>
      </div>

      <div className="gr-grid gr-grid-2">
        <Card
          title="Za co chwalą klienci"
          subtitle="Kategorie pochwał (pogrupowane mocne strony)"
        >
          <BarList
            items={praise.map((t) => ({
              key: t.key,
              label: t.label,
              value: t.praise_mentions,
              secondary: t.top_praise.slice(0, 3).join(" · "),
            }))}
            color={COLORS.positive}
            onSelect={(item) =>
              onDrill({ theme: item.key, themeSide: "praise" })
            }
            emptyText="Brak pochwał w wybranym zakresie"
          />
        </Card>
        <Card
          title="Na co narzekają klienci"
          subtitle="Kategorie skarg (pogrupowane słabe strony)"
        >
          <BarList
            items={complaints.map((t) => ({
              key: t.key,
              label: t.label,
              value: t.complaint_mentions,
              secondary: t.top_complaints.slice(0, 3).join(" · "),
            }))}
            color={COLORS.negative}
            onSelect={(item) =>
              onDrill({ theme: item.key, themeSide: "complaint" })
            }
            emptyText="Brak uwag krytycznych w wybranym zakresie"
          />
        </Card>
      </div>

      {showAttention && (
        <Card
          title="Wymaga uwagi"
          subtitle="Najnowsze negatywne opinie w wybranym zakresie"
          actions={
            attention?.total > 0 && (
              <button
                type="button"
                className="gr-btn gr-btn-ghost"
                onClick={() => onDrill({ sentiment: "negatywna" })}
              >
                Wszystkie ({attention.total}) <ArrowRight size={14} />
              </button>
            )
          }
        >
          {attentionLoading && !attention ? (
            <div className="gr-skeleton-list" />
          ) : attention?.items?.length ? (
            <div className="gr-review-grid">
              {attention.items.map((item) => (
                <ReviewCard
                  key={item.id}
                  item={item}
                  onFilter={onDrill}
                  compact
                />
              ))}
            </div>
          ) : (
            <EmptyState
              title="Brak negatywnych opinii"
              text="W wybranym zakresie nie ma opinii wymagających reakcji."
            />
          )}
        </Card>
      )}
    </div>
  );
};

export default OverviewTab;
